import { useContext, useEffect, useCallback } from "react"
import { ReservationContext } from "source/registrations/reservations/ReservationContext"
import { loadStripe } from "@stripe/stripe-js/pure"
import { patchPaymentIntent } from "source/registrations/api/payments"

const usePaymentIntent = ({
  stripeKey,
  stripeAccount,
  reservation = {},
  allowsOnlinePayments,
}) => {
  const { stripe, setStripe, setPaymentIntent, paymentIntent } = useContext(
    ReservationContext,
  )

  useEffect(() => {
    const fetchStripe = async () => {
      const response = await loadStripe(stripeKey, { stripeAccount })
      setStripe(response)
    }

    if (allowsOnlinePayments && stripeKey && stripeAccount && !stripe)
      fetchStripe()
  }, [stripeKey, stripeAccount, allowsOnlinePayments, stripe, setStripe])

  useEffect(() => {
    if (!reservation.paymentIntent || paymentIntent.data) return

    setPaymentIntent({
      loading: false,
      error: null,
      data: reservation.paymentIntent,
      patch,
      onChange,
    })
  }, [
    reservation.paymentIntent,
    paymentIntent.data,
    setPaymentIntent,
    onChange,
    patch,
  ])

  const patch = useCallback(
    async (amount) => {
      setPaymentIntent({
        ...paymentIntent,
        loading: true,
      })

      const response = await patchPaymentIntent(
        reservation.eventId,
        reservation.id,
        {
          data: { attributes: { amount } },
          fields: { PaymentIntent: "amount,amount_formatted" },
        },
      )

      if (response.errors) {
        setPaymentIntent({
          ...paymentIntent,
          loading: false,
          error: response.errors[0]?.detail,
        })
      } else {
        setPaymentIntent({
          ...paymentIntent,
          loading: false,
          error: null,
          data: { ...paymentIntent.data, ...response.data },
        })
      }
    },
    [reservation, paymentIntent, setPaymentIntent],
  )

  const onChange = useCallback(
    (intent) => {
      if (!intent) return

      setPaymentIntent({
        loading: false,
        error: null,
        data: { ...paymentIntent.data, ...intent },
        patch,
        onChange,
      })
    },
    [paymentIntent.data, setPaymentIntent, patch],
  )

  return {
    ...paymentIntent,
    patch,
    onChange,
  }
}

export default usePaymentIntent
